import { VisualizationMode } from "./types";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { useUser } from "~/modules/user/hooks/useUser";
import { FeatureFlagState } from "~/modules/feature_flag/types";
import { useState } from "react";
import * as Analytics from "~/modules/visualization/analytics";
import { LoginRequiredModal } from "~/modules/downloads/components/LoginRequiredModal";
import { SelectInput } from "~/components/SelectInput";
import { ButtonGroup } from "~/components/ButtonGroup";

// // // //

export interface VisualizationModeControls {
    activeMode: VisualizationMode; // Currently active visualization mode.
    modes: VisualizationMode[]; // Array of all available visualization modes.
    loading: boolean; // Indicates if the mode change is in progress.
    setActiveMode: (updated: VisualizationMode) => void; // Function to update the active mode.
}

// // // //

/**
 * Removes feature-flagged VisualizationMode items if a user isn't allowed to view them
 */
function removeFeatureFlaggedModes(props: {
    modes: VisualizationMode[];
    featureFlagState: FeatureFlagState;
}): VisualizationMode[] {
    const { modes, featureFlagState } = props;

    // Filter groups + nested items based on featureFlagState
    return modes.filter((mode) => {
        const featureFlag = mode.featureFlag;
        if (featureFlag) {
            return featureFlagState[featureFlag];
        }
        return true; // If there's no feature flag specified, include the item
    });
}

// // // //

/**
 * Renders a selector for changing between different visualization modes
 */
export function VisualizationModeSelector(props: {
    modeControls: VisualizationModeControls;
}) {
    const { activeMode: active, setActiveMode: onChange } = props.modeControls;

    const { user, isLoggedIn } = useUser();
    const [showLoginModal, setShowLoginModal] = useState(false);

    // Get the filtered groups based on the user's feature flags
    const modes = removeFeatureFlaggedModes({
        modes: props.modeControls.modes,
        featureFlagState: user.features,
    });

    // Do not render the component if there are less than two modes to switch between.
    if (modes.length < 2) {
        return null;
    }

    // Render a button for each mode
    return (
        <>
            <VisualizationModeSelectorMobile
                {...props}
                isLoggedIn={isLoggedIn}
                showLoginModal={(modeKey) => {
                    Analytics.trackTimeSeriesLoginCtaOpen(modeKey);
                    setShowLoginModal(true);
                }}
            />

            {/* The `@*:` variants here resolve against the `@container` on the
                TimeseriesCard control bar — i.e. the width of the card. */}
            <div className="hidden @2xl:flex items-center gap-2">
                <ButtonGroup
                    title="Metric"
                    value={active.key}
                    onValueChange={(nextModeKey) => {
                        // If the selected mode is already active, do not allow the change
                        const isActive = nextModeKey === active.key;
                        if (isActive) return;

                        // Lookup the mode by key
                        const nextMode = modes.find(
                            (m) => m.key === nextModeKey,
                        );

                        // If the mode is not found, do not allow the change
                        if (!nextMode) {
                            return;
                        }

                        // If the selected mode requires a login and the user is not logged in, do not allow the change
                        if (nextMode.loginRequired && !isLoggedIn) {
                            Analytics.trackTimeSeriesLoginCtaOpen(nextMode.key);
                            setShowLoginModal(true);
                            return;
                        }

                        // Update the active mode
                        onChange(nextMode);
                    }}
                    options={modes.map((m) => {
                        return {
                            label: m.content.label,
                            value: m.key,
                            featureFlag: m.featureFlag,
                            iconClassName: "h-2.5",
                            icon:
                                m.loginRequired && !isLoggedIn
                                    ? faLock
                                    : undefined,
                        };
                    })}
                />
            </div>

            {/* Disply active mode's description text */}
            {/* ENHANCEMENT - re-enable this in a way that doesn't break up the UI */}
            {/* {active.content.description && (
                <div className="mt-2 font-light text-slate-600 text-sm flex items-center gap-1.5">
                    <FontAwesomeIcon className="h-4" icon={faInfoCircle} />
                    {active.content.description}
                </div>
            )} */}

            {!isLoggedIn && (
                <LoginRequiredModal
                    allowScroll // NOTE - this is very important. When this is set to false, all the visualizations will resize and performance will crawl for a second.
                    show={showLoginModal}
                    onHide={() => {
                        setShowLoginModal(false);
                    }}
                    analytics={{
                        onConfirmed: () => {
                            Analytics.trackTimeSeriesLoginCtaConfirmed();
                        },
                        onClose: () => {
                            Analytics.trackTimeSeriesLoginCtaClose();
                        },
                    }}
                />
            )}
        </>
    );
}

/**
 * VisualizationModeSelectorMobile
 * Mobile-only mode selector UI
 */
function VisualizationModeSelectorMobile(props: {
    modeControls: VisualizationModeControls;
    isLoggedIn: boolean;
    showLoginModal: (modeKey: string) => void;
}) {
    const { isLoggedIn, showLoginModal } = props;
    const {
        activeMode: active,
        modes,
        setActiveMode: onChange,
    } = props.modeControls;

    return (
        <div className="@2xl:hidden w-full">
            <SelectInput
                size="sm"
                value={active.key}
                placeholder="Metric"
                placeholderPosition="inset"
                onChange={(updatedValue) => {
                    const modeKey = updatedValue;
                    const selectedMode = modes.find((m) => m.key === modeKey);
                    if (!selectedMode) {
                        return;
                    }

                    // If the selected mode requires a login and the user is not logged in, do not allow the change
                    if (selectedMode.loginRequired && !isLoggedIn) {
                        showLoginModal(selectedMode.key);
                        return;
                    }

                    onChange(selectedMode);
                }}
                options={modes.map((mode) => ({
                    value: mode.key,
                    featureFlag: mode.featureFlag,
                    content: {
                        label:
                            mode.loginRequired && !isLoggedIn
                                ? `${mode.content.label} (Login Required)`
                                : mode.content.label,
                    },
                }))}
            />
        </div>
    );
}
